import { useState, useEffect, useMemo, useCallback } from "react";
import { MemberTableDatatypes } from "@/types/member";
import Combobox from "@/components/ui/common/combobox";

interface CustomerComboboxTypes {
  customerList?: MemberTableDatatypes[];
  setCustomer: (customer: MemberTableDatatypes | null) => void;
  customer: MemberTableDatatypes | null;
  label?: string;
  disable?: boolean;
  className?: string;
}

export function CustomerCombobox({
  customerList,
  setCustomer,
  customer,
  className,
  label = "Select customer",
  disable = false,
}: Readonly<CustomerComboboxTypes>) {
  const [selectedValue, setSelectedValue] = useState<string>("");

  // Transform customer list to include name and member_id for display and search
  const customerOptions = useMemo(() => {
    return customerList?.map((item) => ({
      value: item.id.toString(),
      label: `${item.own_member_id ? `${item.own_member_id} • ` : ''}${item.first_name} ${item.last_name}`,
    })) || [];
  }, [customerList]);

  // Only update selectedValue when customer actually changes (by ID)
  useEffect(() => {
    const newValue = customer?.id?.toString() || "";
    // Only update if the value actually changed to prevent unnecessary renders
    if (newValue !== selectedValue) {
      setSelectedValue(newValue);
    }
  }, [customer?.id]); // Only depend on customer.id, not the entire customer object

  // Memoize the default value calculation
  const defaultValue = useMemo(() => {
    if (!selectedValue) return "";
    const option = customerOptions.find(opt => opt.value === selectedValue);
    return option?.label || "";
  }, [selectedValue, customerOptions]);

  // Memoize the customer selection handler to prevent unnecessary re-renders
  const handleCustomerSelect = useCallback((selectedLabel: string) => {
    // Find the customer based on the selected label
    const selectedCustomer = customerList?.find((item) => {
      const fullLabel = `${item.own_member_id ? `${item.own_member_id} • ` : ''}${item.first_name} ${item.last_name}`;
      return fullLabel === selectedLabel;
    });

    if (selectedCustomer) {
      // If clicking the same customer, deselect
      if (selectedCustomer.id?.toString() === selectedValue) {
        setSelectedValue("");
        setCustomer(null);
      } else {
        setSelectedValue(selectedCustomer.id?.toString() || "");
        setCustomer(selectedCustomer);
      }
    }
  }, [customerList, selectedValue, setCustomer]);

  return (
    <Combobox
      list={customerOptions}
      defaultValue={defaultValue}
      setFilter={handleCustomerSelect}
      label={label}
      disabled={disable}
      className={className+" w-full"}
      showLabel={false}
    />
  );
}
